import { useState, useEffect } from "react";
import { useData } from "@/contexts/DataContext";
import { useAuth } from "@/hooks/use-auth";
import { Printer, Bluetooth, Usb, ChevronRight, Loader2, ChevronLeft, Tag, CheckCircle2, Sparkles, HelpCircle } from "lucide-react";
import { Dialog as NativeDialog } from '@capacitor/dialog';
import { Link, useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import {
  getCachedBTPrinter,
  clearBTPrinterCache,
  connectBTPrinterById,
  getPrinterServiceFilter,
} from "@/lib/btPrinter";
import { BTScanDialog } from "@/components/BTScanDialog";
import BarcodeRenderer from "react-barcode";
import {
  connectUSBPrinter,
  getCachedUSBPrinter,
  clearUSBPrinterCache,
} from "@/lib/usbPrinter";

type WizardStep = "idle" | "select_connection" | "connecting" | "testing_tspl" | "asking_tspl" | "testing_zpl" | "asking_zpl" | "testing_escpos" | "asking_escpos" | "success" | "failed";

export default function PrinterSettings() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const {
    printerPaperWidth,
    setPrinterPaperWidth,
    labelPrinterLanguage,
    setLabelPrinterLanguage,
    labelWidth,
    labelHeight,
    setLabelSize,
    isUltimateOwnerMobile,
  } = useData();
  const { toast } = useToast();

  // Receipt Printer States
  const [btPrinterStatus, setBtPrinterStatus] = useState<"idle" | "connecting" | "connected" | "error">("idle");
  const [btPrinterName, setBtPrinterName] = useState<string | null>(null);
  const [scanFor, setScanFor] = useState<"receipt" | "label" | null>(null);
  const [usbPrinterStatus, setUsbPrinterStatus] = useState<"idle" | "connecting" | "connected" | "error">("idle");
  const [usbPrinterName, setUsbPrinterName] = useState<string | null>(null);

  // Label Printer States
  const [btLabelPrinterStatus, setBtLabelPrinterStatus] = useState<"idle" | "connecting" | "connected" | "error">("idle");
  const [btLabelPrinterName, setBtLabelPrinterName] = useState<string | null>(null);
  const [usbLabelPrinterStatus, setUsbLabelPrinterStatus] = useState<"idle" | "connecting" | "connected" | "error">("idle");
  const [usbLabelPrinterName, setUsbLabelPrinterName] = useState<string | null>(null);

  const [testPrintStatus, setTestPrintStatus] = useState<"idle" | "printing" | "done" | "error">("idle");
  const [labelTestPrintStatus, setLabelTestPrintStatus] = useState<"idle" | "printing" | "done" | "error">("idle");

  // Wizard States
  const [wizardStep, setWizardStep] = useState<WizardStep>("idle");
  const [wizardConnectionType, setWizardConnectionType] = useState<"bt" | "usb" | null>(null);

  useEffect(() => {
    if (user?.uid) {
      const bt = getCachedBTPrinter(user.uid, "receipt");
      if (bt) {
        setBtPrinterStatus("connected");
        setBtPrinterName(bt.name);
      }
      const usb = getCachedUSBPrinter(user.uid, "receipt");
      if (usb) {
        setUsbPrinterStatus("connected");
        setUsbPrinterName(usb.device?.productName || "USB Printer");
      }

      const btLabel = getCachedBTPrinter(user.uid, "label");
      if (btLabel) {
        setBtLabelPrinterStatus("connected");
        setBtLabelPrinterName(btLabel.name);
      }
      const usbLabel = getCachedUSBPrinter(user.uid, "label");
      if (usbLabel) {
        setUsbLabelPrinterStatus("connected");
        setUsbLabelPrinterName(usbLabel.device?.productName || "USB Printer");
      }
    }
  }, [user?.uid]);

  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      setLocation("/");
    }
  };

  // ─── AUTO-DETECT WIZARD LOGIC ───
  const startWizard = () => {
    setWizardStep("select_connection");
  };

  const startWizardBT = () => {
    // Reuse the unified BT scan dialog — consistent UX with the manual Connect
    // buttons. After selection the wizard auto-runs TSPL → ZPL → ESC/POS tests.
    setWizardConnectionType("bt");
    setScanFor("label");
  };

  const startWizardUSB = async () => {
    setWizardStep("connecting");
    setWizardConnectionType("usb");
    try {
      const { connectUSBPrinter } = await import("@/lib/usbPrinter");
      const conn = await connectUSBPrinter(user?.uid, "label");
      setUsbLabelPrinterStatus("connected");
      setUsbLabelPrinterName(conn.device?.productName || "USB Printer");
      setTimeout(() => triggerTSPLTest("usb"), 1000);
    } catch (err: any) {
      setWizardStep("idle");
      await NativeDialog.alert({ title: 'USB Connection Failed', message: err.message || 'Could not connect to a USB printer.' });
    }
  };

  const triggerTSPLTest = async (overrideType?: "bt" | "usb") => {
    const connType = overrideType || wizardConnectionType;
    setWizardStep("testing_tspl");
    try {
      const tsplPayload = new TextEncoder().encode("SIZE 50 mm, 25 mm\r\nGAP 2 mm, 0\r\nCLS\r\nBARCODE 10,10,\"128\",80,1,0,2,2,\"DH00000000TEST\"\r\nPRINT 1\r\n");
      if (connType === "usb") {
        const { sendToUSBPrinter, getCachedUSBPrinter } = await import("@/lib/usbPrinter");
        const cached = getCachedUSBPrinter(user?.uid, "label");
        if (!cached) throw new Error("USB printer not found");
        await sendToUSBPrinter(cached.device, cached.endpoint, tsplPayload, user?.uid);
      } else {
        const { sendToBTPrinter } = await import("@/lib/btPrinter");
        await sendToBTPrinter(user!.uid, tsplPayload, "label");
      }
      setWizardStep("asking_tspl");
    } catch (err) {
      setWizardStep("failed");
    }
  };

  const handleTsplSuccess = () => {
    setLabelPrinterLanguage("tspl");
    toast({ title: "Printer Saved as Standard Label (TSPL)" });
    setWizardStep("success");
    setTimeout(() => setWizardStep("idle"), 2000);
  };

  const triggerZPLTest = async () => {
    setWizardStep("testing_zpl");
    try {
      const zplPayload = new TextEncoder().encode("^XA\n^FO10,10^BCN,80,Y,N,N^FDDH00000000TEST^FS\n^PQ1\n^XZ\n");
      if (wizardConnectionType === "usb") {
        const { sendToUSBPrinter, getCachedUSBPrinter } = await import("@/lib/usbPrinter");
        const cached = getCachedUSBPrinter(user?.uid, "label");
        if (!cached) throw new Error("USB printer not found");
        await sendToUSBPrinter(cached.device, cached.endpoint, zplPayload, user?.uid);
      } else {
        const { sendToBTPrinter } = await import("@/lib/btPrinter");
        await sendToBTPrinter(user!.uid, zplPayload, "label");
      }
      setWizardStep("asking_zpl");
    } catch (err) {
      setWizardStep("failed");
    }
  };

  const handleZplSuccess = () => {
    setLabelPrinterLanguage("zpl");
    toast({ title: "Printer Saved as Advanced Label (ZPL)" });
    setWizardStep("success");
    setTimeout(() => setWizardStep("idle"), 2000);
  };

  const handleZplFail = () => {
    triggerESCPOS_Test();
  };

  const triggerESCPOS_Test = async () => {
    setWizardStep("testing_escpos");
    try {
      const { buildBarcodeEscPos } = await import("@/lib/barcodeUtils");
      const escposPayload = buildBarcodeEscPos([{
        name: "BARCODE PRINTER TEST",
        barcode: "DH00000000TEST",
        qty: 1
      }]);

      if (wizardConnectionType === "usb") {
        const { sendToUSBPrinter, getCachedUSBPrinter } = await import("@/lib/usbPrinter");
        const cached = getCachedUSBPrinter(user?.uid, "label");
        if (!cached) throw new Error("USB printer not found");
        await sendToUSBPrinter(cached.device, cached.endpoint, escposPayload, user?.uid);
      } else {
        const { sendToBTPrinter } = await import("@/lib/btPrinter");
        await sendToBTPrinter(user!.uid, escposPayload, "label");
      }
      setWizardStep("asking_escpos");
    } catch (err) {
      setWizardStep("failed");
    }
  };

  const handleEscposSuccess = () => {
    setLabelPrinterLanguage("escpos");
    toast({ title: "Printer Saved as Receipt Roll (ESC/POS)" });
    setWizardStep("success");
    setTimeout(() => setWizardStep("idle"), 2000);
  };

  const handleEscposFail = () => {
    setLabelPrinterLanguage("escpos"); // Default fallback
    setWizardStep("failed");
    setTimeout(() => setWizardStep("idle"), 3000);
  };


  const handleTestPrint = async () => {
    setTestPrintStatus("printing");
    try {
      const ESC = 0x1b;
      const GS = 0x1d;
      const testPage: number[] = [
        ESC, 0x40, ESC, 0x61, 1, ESC, 0x21, 0x10,
        ...Array.from("DAILYHISAAB").map((c) => c.charCodeAt(0)),
        0x0a, ESC, 0x21, 0x00,
        ...Array.from("Printer Test OK").map((c) => c.charCodeAt(0)),
        0x0a, ...Array.from("dailyhisaab.com").map((c) => c.charCodeAt(0)),
        0x0a, ESC, 0x61, 0,
        ..."-".repeat(32).split("").map((c) => c.charCodeAt(0)),
        0x0a, ESC, 0x64, 4, GS, 0x56, 0x00,
      ];
      const data = new Uint8Array(testPage);

      if (usbPrinterStatus === "connected") {
        const { sendToUSBPrinter, getCachedUSBPrinter } = await import("@/lib/usbPrinter");
        const cached = getCachedUSBPrinter(user?.uid, "receipt");
        if (cached) {
          await sendToUSBPrinter(cached.device, cached.endpoint, data);
        } else {
          throw new Error("USB connection lost.");
        }
      } else if (btPrinterStatus === "connected") {
        const { sendToBTPrinter } = await import("@/lib/btPrinter");
        if (user?.uid) {
          await sendToBTPrinter(user.uid, data, "receipt");
        }
      }

      setTestPrintStatus("done");
      setTimeout(() => setTestPrintStatus("idle"), 3000);
      await NativeDialog.alert({ title: 'Printer', message: 'Test page sent successfully!' });
    } catch (err: any) {
      setTestPrintStatus("error");
      setTimeout(() => setTestPrintStatus("idle"), 3000);
      await NativeDialog.alert({ title: 'Printer Error', message: err.message });
    }
  };

  const handleLabelTestPrint = async () => {
    setLabelTestPrintStatus("printing");
    try {
      let data: Uint8Array;
      if (labelPrinterLanguage === "tspl") {
        data = new TextEncoder().encode(`SIZE ${labelWidth} mm, ${labelHeight} mm\r\nGAP 2 mm, 0\r\nCLS\r\nTEXT 10,10,"3",0,1,1,"BARCODE PRINTER TEST"\r\nBARCODE 10,50,"128",80,1,0,2,2,"DAILYHISAAB"\r\nPRINT 1\r\n`);
      } else if (labelPrinterLanguage === "zpl") {
        data = new TextEncoder().encode("^XA\n^FO10,10^A0N,25,25^FDBARCODE PRINTER TEST^FS\n^FO10,50^BCN,80,Y,N,N^FDDH00000000TEST^FS\n^PQ1\n^XZ\n");
      } else {
        const { buildBarcodeEscPos } = await import("@/lib/barcodeUtils");
        data = buildBarcodeEscPos([{
          name: "BARCODE PRINTER TEST",
          barcode: "DH00000000TEST",
          qty: 1
        }]);
      }

      if (usbLabelPrinterStatus === "connected") {
        const { sendToUSBPrinter, getCachedUSBPrinter } = await import("@/lib/usbPrinter");
        const cached = getCachedUSBPrinter(user?.uid, "label");
        if (cached) {
          await sendToUSBPrinter(cached.device, cached.endpoint, data, user?.uid);
        } else {
          throw new Error("USB connection lost.");
        }
      } else if (btLabelPrinterStatus === "connected") {
        const { sendToBTPrinter } = await import("@/lib/btPrinter");
        await sendToBTPrinter(user!.uid, data, "label");
      }

      setLabelTestPrintStatus("done");
      setTimeout(() => setLabelTestPrintStatus("idle"), 3000);
      await NativeDialog.alert({ title: 'Printer', message: 'Test barcode sent successfully!' });
    } catch (err: any) {
      setLabelTestPrintStatus("error");
      setTimeout(() => setLabelTestPrintStatus("idle"), 3000);
      await NativeDialog.alert({ title: 'Printer Error', message: err.message });
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#FFFBF2] dark:bg-[#1A1614]">
      {/* ─── WIZARD OVERLAY ─── */}
      {wizardStep !== "idle" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-900 rounded-[2rem] p-6 w-full max-w-sm shadow-2xl flex flex-col items-center text-center">

            {wizardStep === "select_connection" && (
              <>
                <div className="w-16 h-16 bg-amber-50 dark:bg-amber-900/30 rounded-full flex items-center justify-center mb-4 border border-amber-100 dark:border-amber-800">
                  <Printer className="h-8 w-8 text-amber-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Connect Printer</h3>
                <p className="text-sm text-slate-500 mb-6 px-2">How is your label printer connected to your device?</p>
                <div className="flex flex-col gap-3 w-full">
                  <button
                    onClick={startWizardBT}
                    className="w-full h-14 rounded-2xl font-black uppercase tracking-widest text-[12px] bg-[#8B1A1A] hover:bg-[#631212] text-white flex items-center justify-center gap-2 shadow-lg shadow-red-900/20 active:scale-95 transition-all"
                  >
                    <Bluetooth className="w-5 h-5" /> Bluetooth
                  </button>
                  <button
                    onClick={startWizardUSB}
                    className="w-full h-14 rounded-2xl font-black uppercase tracking-widest text-[12px] border-2 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center justify-center gap-2 active:scale-95 transition-all bg-transparent"
                  >
                    <Usb className="w-5 h-5" /> USB Cable
                  </button>
                </div>
                <button onClick={() => setWizardStep("idle")} className="mt-4 text-xs font-bold text-slate-400 uppercase tracking-widest p-2">Cancel</button>
              </>
            )}

            {wizardStep === "connecting" && (
              <>
                <div className="w-16 h-16 bg-blue-50 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
                  {wizardConnectionType === "usb" ? (
                    <Usb className="h-8 w-8 text-blue-600 animate-pulse" />
                  ) : (
                    <Bluetooth className="h-8 w-8 text-blue-600 animate-pulse" />
                  )}
                </div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Connecting...</h3>
                <p className="text-sm text-slate-500">Please select your printer to connect.</p>
              </>
            )}

            {(wizardStep === "testing_tspl" || wizardStep === "testing_zpl" || wizardStep === "testing_escpos") && (
              <>
                <div className="w-16 h-16 bg-amber-50 dark:bg-amber-900/30 rounded-full flex items-center justify-center mb-4">
                  <Loader2 className="h-8 w-8 text-amber-600 animate-spin" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Sending Test Print...</h3>
                <p className="text-sm text-slate-500">Watch your printer closely.</p>
              </>
            )}

            {(wizardStep === "asking_tspl" || wizardStep === "asking_zpl" || wizardStep === "asking_escpos") && (
              <>
                <div className="w-16 h-16 bg-amber-50 dark:bg-amber-900/30 rounded-full flex items-center justify-center mb-4 border border-amber-100 dark:border-amber-800">
                  <HelpCircle className="h-8 w-8 text-amber-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                  {wizardStep === "asking_tspl" ? "Did it print a barcode?" : wizardStep === "asking_zpl" ? "Trying Plan B..." : "Trying Plan C..."}
                </h3>

                <div className="w-full flex flex-col items-center justify-center py-4 px-6 bg-white dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 border-dashed rounded-xl mb-4 shadow-inner">
                  <p className="text-[10px] font-bold text-slate-900 dark:text-white uppercase mb-1">BARCODE PRINTER TEST</p>

                  <div className="dark:invert opacity-90">
                    <BarcodeRenderer
                      value="DH00000000TEST"
                      format="CODE128"
                      width={1.5}
                      height={45}
                      displayValue={false}
                      background="transparent"
                    />
                  </div>

                  <p className="text-[12px] font-mono tracking-[0.25em] mt-1 text-slate-900 dark:text-white">DH00000000TEST</p>
                </div>

                <p className="text-sm text-slate-500 mb-6 font-medium leading-relaxed">
                  Does the paper coming out look <strong className="text-slate-800 dark:text-slate-200">exactly like a barcode</strong> as shown above? <br /><br />
                  <span className="text-xs text-red-500 dark:text-red-400 font-bold block bg-red-50 dark:bg-red-900/10 p-2 rounded-lg border border-red-100 dark:border-red-900/30">
                    (If it just printed english text like "SIZE 50mm", tap No)
                  </span>
                </p>

                <div className="flex gap-3 w-full">
                  <button
                    onClick={() => {
                      if (wizardStep === "asking_tspl") triggerZPLTest();
                      else if (wizardStep === "asking_zpl") triggerESCPOS_Test();
                      else handleEscposFail();
                    }}
                    className="flex-1 py-3.5 rounded-xl border-2 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 font-bold active:scale-95 transition-transform bg-white dark:bg-slate-900">
                    No, it's text
                  </button>
                  <button
                    onClick={() => {
                      if (wizardStep === "asking_tspl") handleTsplSuccess();
                      else if (wizardStep === "asking_zpl") handleZplSuccess();
                      else handleEscposSuccess();
                    }}
                    className="flex-1 py-3.5 rounded-xl bg-[#8B1A1A] hover:bg-[#631212] text-white font-bold shadow-md active:scale-95 transition-transform">
                    Yes, barcode!
                  </button>
                </div>
              </>
            )}

            {wizardStep === "success" && (
              <>
                <div className="w-16 h-16 bg-green-50 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle2 className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Setup Complete!</h3>
                <p className="text-sm text-slate-500">Your printer language has been saved successfully.</p>
              </>
            )}

            {wizardStep === "failed" && (
              <>
                <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4">
                  <Printer className="h-8 w-8 text-slate-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">It's a Receipt Printer!</h3>
                <p className="text-sm text-slate-500 mb-6">Your printer uses standard receipt paper. We've updated your settings.</p>
              </>
            )}

          </div>
        </div>
      )}

      {isUltimateOwnerMobile ? (
        <div className="px-4 pb-2" style={{ paddingTop: "calc(env(safe-area-inset-top) + 1.25rem)" }}>
          <div className="flex items-start gap-3">
            <button onClick={handleBack} className="rounded-full p-2 border border-slate-200 dark:border-[#454545] bg-white dark:bg-[#1F1B19]/60 mt-1 shrink-0">
              <ChevronLeft className="h-4 w-4 text-[#0F172A] dark:text-[#EEEAE3]" />
            </button>
            <div className="min-w-0">
              <h1 className="text-2xl font-black uppercase tracking-tighter leading-[0.95] text-[#0F172A] dark:text-[#EEEAE3]">
                PRINTER<br /><span className="text-[#8B1A1A]">SETUP</span>
              </h1>
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-[#8A8A92] mt-2">
                Receipt · Label · Barcode
              </p>
            </div>
          </div>
        </div>
      ) : (
        <header className="px-4 py-4 flex items-center sticky top-0 z-30 bg-[#FFFBF2]/90 dark:bg-[#1A1614]/90 backdrop-blur-md">
          <button onClick={handleBack} className="w-10 h-10 bg-white dark:bg-slate-800 rounded-full flex items-center justify-center shadow-sm border border-slate-100 dark:border-slate-700 active:scale-95 transition-transform shrink-0">
            <ChevronLeft className="w-6 h-6 text-slate-800 dark:text-white" />
          </button>
          <h1 className="text-lg font-black text-slate-900 dark:text-white ml-4 tracking-tight">Printer Setup</h1>
        </header>
      )}

      <div className="flex-1 px-4 py-4 pb-24 overflow-y-auto">

        {/* === RECEIPT PRINTER SECTION === */}
        <h2 className="text-sm font-black text-slate-800 dark:text-white uppercase tracking-widest mb-3 ml-1 flex items-center gap-2">
          <Printer className="h-4 w-4" /> Standard Receipt Printer
        </h2>

        <div className="bg-white dark:bg-slate-900 rounded-3xl p-5 border border-slate-100 dark:border-slate-800 shadow-sm mb-4">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Receipt Paper Size</p>
          <div className="flex gap-2 mb-6">
            {(["58", "80"] as const).map((w) => (
              <button
                key={w}
                onClick={() => {
                  setPrinterPaperWidth(w);
                  toast({ title: `Paper size set to ${w}mm`, duration: 2000 });
                }}
                className={`flex-1 py-3 rounded-2xl text-sm font-bold border-2 transition-all ${printerPaperWidth === w
                  ? "border-[#8B2121] bg-[#8B2121] text-white"
                  : "border-slate-100 dark:border-slate-800 text-slate-600 bg-slate-50 dark:bg-slate-800/50"
                  }`}
              >
                {w} mm
              </button>
            ))}
          </div>

          {/* BT Receipt */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-[#8B1A1A]/15 flex items-center justify-center">
              <Bluetooth className="h-5 w-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-slate-900 dark:text-white text-sm">Bluetooth</p>
              <p className="text-[11px] text-slate-400">{btPrinterName || "Not paired"}</p>
            </div>
            {btPrinterStatus === "connected" ? (
              <button onClick={() => { clearBTPrinterCache(user!.uid, "receipt"); setBtPrinterStatus("idle"); setBtPrinterName(null); }} className="text-xs font-bold text-red-600 px-4 py-2 rounded-xl bg-red-50">Forget</button>
            ) : (
              <button
                disabled={btPrinterStatus === "connecting"}
                onClick={() => setScanFor("receipt")}
                className="text-xs font-bold text-blue-600 px-4 py-2 rounded-xl bg-blue-50"
              >
                {btPrinterStatus === "connecting" ? "..." : "Connect"}
              </button>
            )}
          </div>

          {/* USB Receipt */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-50 dark:bg-orange-950/30 flex items-center justify-center">
              <Usb className="h-5 w-5 text-orange-600" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-slate-900 dark:text-white text-sm">USB Connection</p>
              <p className="text-[11px] text-slate-400">{usbPrinterName || "Not connected"}</p>
            </div>
            {usbPrinterStatus === "connected" ? (
              <button onClick={() => { clearUSBPrinterCache(user!.uid, "receipt"); setUsbPrinterStatus("idle"); setUsbPrinterName(null); }} className="text-xs font-bold text-red-600 px-4 py-2 rounded-xl bg-red-50">Forget</button>
            ) : (
              <button
                disabled={usbPrinterStatus === "connecting"}
                onClick={async () => {
                  setUsbPrinterStatus("connecting");
                  try {
                    const conn = await connectUSBPrinter(user?.uid, "receipt");
                    setUsbPrinterStatus("connected");
                    setUsbPrinterName(conn.device?.productName || "USB Printer");
                  } catch (err: any) {
                    setUsbPrinterStatus("error");
                    await NativeDialog.alert({ title: 'USB Error', message: err.message });
                  }
                }}
                className="text-xs font-bold text-orange-600 px-4 py-2 rounded-xl bg-orange-50"
              >
                {usbPrinterStatus === "connecting" ? "..." : "Connect"}
              </button>
            )}
          </div>
        </div>

        {/* ── RECEIPT TEST PRINT BUTTON ── */}
        {(btPrinterStatus === "connected" || usbPrinterStatus === "connected") && (
          <div className="px-1 mb-8 mt-[-8px]">
            <button
              onClick={handleTestPrint}
              disabled={testPrintStatus === "printing"}
              className="w-full py-4 rounded-[2rem] bg-[#8B2121] text-white font-bold text-sm shadow-lg shadow-red-900/20 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
            >
              {testPrintStatus === "printing" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Printer className="h-4 w-4" />
              )}
              {testPrintStatus === "printing" ? "Sending to Printer..." : "Test Receipt Print"}
            </button>
          </div>
        )}

        {/* === BARCODE / LABEL PRINTER SECTION === */}
        <div className="mt-8 mb-3 ml-1 flex items-center justify-between">
          <h2 className="text-sm font-black text-slate-800 dark:text-white uppercase tracking-widest flex items-center gap-2">
            <Tag className="h-4 w-4" /> Barcode & Label Printer
          </h2>
          <span className="text-[9px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-md uppercase tracking-wider">Inventory</span>
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-3xl p-5 border border-slate-100 dark:border-slate-800 shadow-sm mb-6">

          {/* THE GUIDED TEST WIZARD BUTTON */}
          <button
            onClick={startWizard}
            className="w-full mb-6 py-4 px-4 rounded-2xl border-2 border-amber-500 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 flex items-center justify-between group active:scale-[0.98] transition-transform"
          >
            <div className="flex items-center gap-3 text-left">
              <Sparkles className="h-6 w-6 text-amber-500" />
              <div>
                <p className="font-bold text-sm">Don't know your printer type?</p>
                <p className="text-xs opacity-80">Run our Auto-Detect Wizard</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 opacity-50 group-hover:opacity-100 transition-opacity" />
          </button>

          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Manual Printer Type</p>
          <div className="flex flex-col gap-3 mb-6">
            {[
              { id: "tspl", name: "Standard Label Printer", desc: "Most common for basic barcode sticker printers." },
              { id: "zpl", name: "Advanced Label Printer", desc: "For premium or industrial barcode printers." },
              { id: "escpos", name: "Receipt Printer", desc: "Print barcodes on regular continuous paper rolls." }
            ].map((option) => (
              <button
                key={option.id}
                onClick={() => {
                  setLabelPrinterLanguage(option.id as any);
                  toast({ title: `Updated to ${option.name}`, duration: 2000 });
                }}
                className={`text-left p-3 rounded-xl border-2 transition-all flex flex-col gap-1 ${labelPrinterLanguage === option.id
                  ? "border-teal-600 bg-teal-50 dark:bg-teal-900/20"
                  : "border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-slate-200"
                  }`}
              >
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-bold ${labelPrinterLanguage === option.id ? "text-teal-700 dark:text-teal-400" : "text-slate-700 dark:text-slate-300"}`}>
                    {option.name}
                  </span>
                  {labelPrinterLanguage === option.id && <CheckCircle2 className="h-4 w-4 text-teal-600" />}
                </div>
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {option.desc}
                </span>
              </button>
            ))}
          </div>

          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-6 mb-4">Sticker Size</p>
          <div className="grid grid-cols-2 gap-2 mb-6">
            {[
              { w: 50, h: 25, label: '2" x 1"' },
              { w: 38, h: 50, label: '1.5" x 2"' },
              { w: 58, h: 40, label: '58mm Roll' }, // <-- ADDED 58mm PRESET TILE
              { w: 40, h: 30, label: 'Standard' }
            ].map((size) => {
              const isSelected = labelWidth === size.w && labelHeight === size.h;
              return (
                <button
                  key={size.label}
                  onClick={() => {
                    if (setLabelSize) {
                      setLabelSize(size.w, size.h);
                      toast({ title: `Size set to ${size.w}x${size.h}mm`, duration: 2000 });
                    }
                  }}
                  className={`py-3 rounded-xl text-xs font-bold border-2 transition-all flex flex-col items-center gap-1 ${isSelected
                    ? "border-amber-600 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400"
                    : "border-slate-100 dark:border-slate-800 text-slate-600 bg-slate-50 dark:bg-slate-800/50 hover:border-slate-200"
                    }`}
                >
                  <span>{size.w}mm x {size.h}mm</span>
                  <span className="text-[10px] font-medium opacity-70">{size.label}</span>
                </button>
              );
            })}
          </div>

          {/* BT Label */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-[#8B1A1A]/15 flex items-center justify-center">
              <Bluetooth className="h-5 w-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-slate-900 dark:text-white text-sm">Bluetooth</p>
              <p className="text-[11px] text-slate-400">{btLabelPrinterName || "Not paired"}</p>
            </div>
            {btLabelPrinterStatus === "connected" ? (
              <button onClick={() => { clearBTPrinterCache(user!.uid, "label"); setBtLabelPrinterStatus("idle"); setBtLabelPrinterName(null); }} className="text-xs font-bold text-red-600 px-4 py-2 rounded-xl bg-red-50">Forget</button>
            ) : (
              <button
                disabled={btLabelPrinterStatus === "connecting"}
                onClick={() => setScanFor("label")}
                className="text-xs font-bold text-blue-600 px-4 py-2 rounded-xl bg-blue-50"
              >
                {btLabelPrinterStatus === "connecting" ? "..." : "Connect"}
              </button>
            )}
          </div>

          {/* USB Label */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-50 dark:bg-orange-950/30 flex items-center justify-center">
              <Usb className="h-5 w-5 text-orange-600" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-slate-900 dark:text-white text-sm">USB Connection</p>
              <p className="text-[11px] text-slate-400">{usbLabelPrinterName || "Not connected"}</p>
            </div>
            {usbLabelPrinterStatus === "connected" ? (
              <button onClick={() => { clearUSBPrinterCache(user!.uid, "label"); setUsbLabelPrinterStatus("idle"); setUsbLabelPrinterName(null); }} className="text-xs font-bold text-red-600 px-4 py-2 rounded-xl bg-red-50">Forget</button>
            ) : (
              <button
                disabled={usbLabelPrinterStatus === "connecting"}
                onClick={async () => {
                  setUsbLabelPrinterStatus("connecting");
                  try {
                    const conn = await connectUSBPrinter(user?.uid, "label");
                    setUsbLabelPrinterStatus("connected");
                    setUsbLabelPrinterName(conn.device?.productName || "USB Printer");
                  } catch (err: any) {
                    setUsbLabelPrinterStatus("error");
                    await NativeDialog.alert({ title: 'USB Error', message: err.message });
                  }
                }}
                className="text-xs font-bold text-orange-600 px-4 py-2 rounded-xl bg-orange-50"
              >
                {usbLabelPrinterStatus === "connecting" ? "..." : "Connect"}
              </button>
            )}
          </div>
        </div>

        {/* ── LABEL TEST PRINT BUTTON ── */}
        {(btLabelPrinterStatus === "connected" || usbLabelPrinterStatus === "connected") && (
          <div className="px-1 mb-6 mt-[-8px]">
            <button
              onClick={handleLabelTestPrint}
              disabled={labelTestPrintStatus === "printing"}
              className="w-full py-4 rounded-[2rem] bg-amber-600 text-white font-bold text-sm shadow-lg shadow-amber-900/20 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
            >
              {labelTestPrintStatus === "printing" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Tag className="h-4 w-4" />
              )}
              {labelTestPrintStatus === "printing" ? "Sending Barcode..." : "Test Barcode Print"}
            </button>
          </div>
        )}

        <Link href="/supported-printers">
          <div className="flex items-center justify-between px-3 py-1 mb-10 group active:opacity-70 transition-opacity">
            <span className="text-xs font-bold text-[#8B2121] dark:text-red-400 uppercase tracking-widest">Supported Models</span>
            <ChevronRight className="h-4 w-4 text-[#8B2121] dark:text-red-400" />
          </div>
        </Link>
      </div>

      {/* Custom BT scan dialog — opens when user taps Connect on either Receipt or Label BT row. */}
      <BTScanDialog
        open={!!scanFor}
        onClose={() => {
          setScanFor(null);
          // If wizard was waiting on this dialog, abort it.
          if (wizardConnectionType === "bt" && wizardStep === "idle") {
            setWizardConnectionType(null);
          }
        }}
        printerType={scanFor || "receipt"}
        printerServices={getPrinterServiceFilter(scanFor || "receipt")}
        onSelect={async (device) => {
          const type = scanFor;
          const isWizardFlow = wizardConnectionType === "bt" && type === "label";
          if (!type || !user?.uid) {
            setScanFor(null);
            return;
          }
          if (isWizardFlow) {
            setWizardStep("connecting");
          } else if (type === "receipt") {
            setBtPrinterStatus("connecting");
          } else {
            setBtLabelPrinterStatus("connecting");
          }
          try {
            const name = await connectBTPrinterById(user.uid, type, {
              deviceId: device.deviceId,
              name: device.name,
            });
            if (type === "receipt") {
              setBtPrinterStatus("connected");
              setBtPrinterName(name);
            } else {
              setBtLabelPrinterStatus("connected");
              setBtLabelPrinterName(name);
            }
            setScanFor(null);
            if (isWizardFlow) {
              // Hand off to the auto-detect test loop (TSPL → ZPL → ESC/POS).
              setTimeout(() => triggerTSPLTest("bt"), 600);
            }
          } catch (err: any) {
            if (isWizardFlow) {
              setWizardStep("idle");
              setWizardConnectionType(null);
            } else if (type === "receipt") {
              setBtPrinterStatus("error");
            } else {
              setBtLabelPrinterStatus("error");
            }
            setScanFor(null);
            await NativeDialog.alert({ title: "Pairing Failed", message: err?.message || String(err) });
          }
        }}
      />
    </div>
  );
}